import { useSession } from "@supabase/auth-helpers-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getIntegrationResources } from "@/common/lib/api";
import { useAccountContext } from "./AccountProvider";
import { IntegrationProvider as IntegrationProviderType } from "@/features/settings/components/integrations/Integrations";
import _ from "lodash";

const IntegrationLoadingErrorMsg = "Error loading integration data. Refresh the page and try again.";
const supportedIntegrationProviders: IntegrationProviderType[] = [
    IntegrationProviderType.Bitwerx,
    IntegrationProviderType.Shepherd,
    IntegrationProviderType.VetSpire,
    IntegrationProviderType.EzyVet,
    IntegrationProviderType.DaySmart,
    IntegrationProviderType.HappyBridge,
];

/**
 * Provides a context for managing and accessing account information throughout the application.
 */
export const IntegrationContext = createContext<
    | {
          canSetupIntegration: boolean;
          accountIsIntegrated: boolean;
          setAccountIsIntegrated: React.Dispatch<React.SetStateAction<boolean>>;
          possibleColumns: any[];
          setPossibleColumns: React.Dispatch<React.SetStateAction<string[]>>;
          possibleProviderIDs: any[];
          setPossibleProviderIDs: React.Dispatch<React.SetStateAction<string[]>>;
          isLoadingIntegrationData: boolean;
          setLoadIntegrationData: React.Dispatch<React.SetStateAction<boolean>>;
          errorLoadingIntegrationData: string | undefined;
          defaultIntegration: IntegrationProviderType | undefined | null;
      }
    | undefined
>(undefined);

/**
 * The IntegrationProvider component fetches and provides account, doctor, PMS integration,
 * and subscription data for children components. It manages state related to the current user's
 * session and data, initializing with session check and data fetching, and provides functions
 * to manipulate this data.
 * @param {React.ReactNode} children - The children components that will consume the context.
 */
export const IntegrationProvider = ({ children }: { children: React.ReactNode }) => {
    // initialize the session using Supabase's useSession hook for user authentication.
    const session = useSession();

    // hooks
    const { accountData, pmsIntegrationData } = useAccountContext();

    // state management
    const [possibleColumns, setPossibleColumns] = useState<any[]>([]);
    const [possibleProviderIDs, setPossibleProviderIDs] = useState<any[]>([]);
    const [accountIsIntegrated, setAccountIsIntegrated] = useState(false);
    const [integrationDataLoading, setIntegrationDataLoading] = useState(false);
    const [loadIntegrationData, setLoadIntegrationData] = useState(false);
    const [errorLoadingIntegrationData, setErrorLoadingIntegrationData] = useState<string | undefined>(undefined);
    const [canSetupIntegration, setCanSetupIntegration] = useState(false);
    const [defaultIntegration, setDefaultIntegration] = useState<IntegrationProviderType | undefined | null>(null);

    // abort controller
    const abortControllerRef = useRef<AbortController | null>(null);

    // Utility function to abort previous requests
    const abortPreviousRequest = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    // Utility function to clean up the AbortController reference
    const cleanUpController = (controller: AbortController) => {
        if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
        }
    };

    // Function to identify expected errors
    const isExpectedError = (error: any) => {
        return error instanceof TypeError && (error.message === "Failed to fetch" || error.message === "Load fail");
    };

    /*
     * Effect to update whether the account has an integration based on pmsIntegrationData from AccountContext.
     */
    useEffect(() => {
        // if we don't have integration data, reset state
        if (!pmsIntegrationData) {
            setAccountIsIntegrated(false);
            setDefaultIntegration(null);
            return;
        }

        // check the account integrations to make sure it has an active integration
        const hasIntegration = pmsIntegrationData.some((integration) => {
            return (
                integration.is_active &&
                supportedIntegrationProviders.includes(
                    integration?.integrations?.integration_name as IntegrationProviderType,
                )
            );
        });

        // if the account has an integration, set the account as integrated
        setAccountIsIntegrated(hasIntegration);

        // find the active integration and set it as the default
        const activeIntegration = pmsIntegrationData.find(
            (integration) =>
                integration.is_active &&
                supportedIntegrationProviders.includes(
                    integration?.integrations?.integration_name as IntegrationProviderType,
                ),
        );
        setDefaultIntegration(activeIntegration?.integrations?.integration_name || null);
    }, [pmsIntegrationData]);

    // True when at least one active supported integration has recorded a successful request.
    // Written server-side on every pull, so it is the automatic equivalent of the old
    // admin-set ENABLE_INTEGRATION_SELF_SETUP flag.
    const integrationHasSucceeded = (pmsIntegrationData ?? []).some(
        (integration) =>
            integration.is_active &&
            integration.last_request_status === "success" &&
            supportedIntegrationProviders.includes(
                integration?.integrations?.integration_name as IntegrationProviderType,
            ),
    );

    // this effect checks whether a user can see the integration modal buttons
    //
    // Self-setup used to require the ENABLE_INTEGRATION_SELF_SETUP feature flag. The flag was
    // removed in EN-1056, but "has an active integration row" turned out to be too weak a
    // replacement: 169 of 171 unmapped ezyVet accounts in prod had a row with no credentials that
    // had never made a single request. The flag was effectively a manual marker for "this
    // integration is actually live", set by an admin at setup time. `last_request_status` is the
    // same fact recorded automatically, so gate on that instead.
    //
    // This deliberately hides the button from ~19 prod accounts that already have a doctor mapping
    // but no successful request on record. That is not a regression to route around: this same
    // condition gates the resource fetch below, and the dropdown options come from a live call to
    // the PIMS, so an integration that has never succeeded cannot populate the modal anyway.
    // Showing the button there would open a form that can never be filled in.
    useEffect(() => {
        setCanSetupIntegration(accountIsIntegrated && integrationHasSucceeded);
    }, [accountIsIntegrated, integrationHasSucceeded]);

    // effect to fetch bitwerx data if the account has the integration
    useEffect(() => {
        // if we don't have an account, don't load the integration data
        if (!accountData) {
            return;
        }

        // if the account is not integrated, don't load the integration data
        if (!accountIsIntegrated) {
            return;
        }

        // if the account doesn't have the ability to set up the integration, don't bother loading the data
        if (!canSetupIntegration) {
            return;
        }

        // if the integration data has not been requested, don't load it
        if (!loadIntegrationData) {
            return;
        }

        // if we have already loaded the integration data, don't load it again
        if (possibleColumns.length > 0 && possibleProviderIDs.length > 0) {
            return;
        }

        // fetches integration data
        const fetchIntegrationData = async () => {
            try {
                if (!session) {
                    return;
                }

                setIntegrationDataLoading(true);

                const response = await getIntegrationResources();

                // set error if response contains an error
                if (response.error) {
                    setErrorLoadingIntegrationData(IntegrationLoadingErrorMsg);
                    throw new Error(response.error);
                }

                // set error if response is not successful
                if (!response.success) {
                    setErrorLoadingIntegrationData(IntegrationLoadingErrorMsg);
                    throw new Error("Unknown error loading integration data.");
                }

                // for now, only support the first integration
                // todo: HDD-1882 allow multiple integrations in settings set up
                const integrationId = Object.keys(response.data || {})[0];
                const integrationData = response.data?.[integrationId];

                // set error if integration data is not defined
                if (!integrationData) {
                    setErrorLoadingIntegrationData(IntegrationLoadingErrorMsg);
                    throw new Error("Data was not defined when loading integration data.");
                }

                // extract the column options
                const columnOptions = _.sortBy(integrationData?.columnData || [], "DisplayName");

                // extract the provider id options, filtering out inactive ones
                const providerIdOptions = _.sortBy(
                    integrationData?.providerIdData?.filter((item: any) => item?.IsInactive !== true) || [],
                    "DisplayName",
                );

                setPossibleColumns(columnOptions);
                setPossibleProviderIDs(providerIdOptions);
            } catch (error) {
                if (!isExpectedError(error)) {
                    Sentry.captureException(error);
                }
            } finally {
                cleanUpController(abortControllerRef.current!);
                setIntegrationDataLoading(false);
                setLoadIntegrationData(false);
            }
        };

        fetchIntegrationData();

        return () => {
            abortPreviousRequest();
        };
    }, [accountData, accountIsIntegrated, loadIntegrationData, session, canSetupIntegration]);

    return (
        <IntegrationContext.Provider
            value={{
                canSetupIntegration,
                accountIsIntegrated,
                setAccountIsIntegrated,
                possibleColumns,
                setPossibleColumns,
                possibleProviderIDs,
                setPossibleProviderIDs,
                isLoadingIntegrationData: integrationDataLoading,
                setLoadIntegrationData,
                errorLoadingIntegrationData,
                defaultIntegration,
            }}
        >
            {children}
        </IntegrationContext.Provider>
    );
};

/**
 * Custom hook to consume account context. It ensures the context is used within its provider.
 * Throws an error if used outside the `IntegrationProvider`.
 * @returns The account context with user's account data, doctors, PMS integration data, subscription info, and utility functions.
 */
export const useIntegrationContext = () => {
    const context = useContext(IntegrationContext);
    if (context === undefined) {
        throw new Error("useIntegrationContext must be used within a IntegrationProvider");
    }
    return context;
};
